// WS5 PR5 — Walk-to-edge auto trigger (pure math).
//
// Quando il player cammina verso il bordo dell'AABB headbox corrente con
// velocity outward persistente, sceglie automaticamente la viewbox piu'
// promettente nella direzione di velocita' e segnala "candidato pronto"
// (= caller decidera' se dispatch START_TO_VIEWBOX guardando cooldown +
// mutex camera). Pattern Unity Oniri_Attractor + Oniri_Manager (radial
// pull static currentAttractor) riadattato per ride-mode discrete.
//
// Pure functions, no side-effect. Stato di tracking (outwardSince ms,
// lastEvalIdx) e' owned dal caller (orchestrator), passato come arg.

import type { ModelViewbox, Vec3 } from '../api/types.js';
import {
  EDGE_CANDIDATE_COSINE_MIN,
  EDGE_CANDIDATE_MAX_M,
  EDGE_NEAR_FACE_M,
  EDGE_OUTWARD_COSINE_MIN,
  EDGE_PERSISTENCE_MS,
  EDGE_VELOCITY_MIN_MPS,
} from './types.js';

/** State di tracking persistenza outward velocity. Owned dall'orchestrator,
 *  inizializzato a `{ outwardSince: null }` su scena load / ride COMPLETE. */
export interface EdgeTrackingState {
  /** Timestamp (perf.now ms) del primo tick con outward true consecutivo.
   *  null = non in tracking (no outward attualmente). */
  outwardSince: number | null;
}

/** Crea uno state iniziale. */
export function initEdgeTracking(): EdgeTrackingState {
  return { outwardSince: null };
}

/** Reset di tracking (call su model unload, ride COMPLETE, viewbox change). */
export function resetEdgeTracking(state: EdgeTrackingState): void {
  state.outwardSince = null;
}

/** Dot product 2D. */
function dot2(a: readonly [number, number], b: readonly [number, number]): number {
  return a[0] * b[0] + a[1] * b[1];
}

/** Magnitude 2D. */
function mag2(v: readonly [number, number]): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1]);
}

/** Normalize 2D, ritorna [0,0] se input degenere. */
function normalize2(v: readonly [number, number]): readonly [number, number] {
  const m = mag2(v);
  if (m < 1e-9) return [0, 0];
  return [v[0] / m, v[1] / m];
}

/** Distanza minima 2D (XZ) dal punto a una face dell'AABB headbox.
 *  Considera solo X e Z (Y e' bound dal floor/eyeHeight, non rilevante per edge).
 *
 *  Ritorna: distanza signed positiva. 0 se sul bordo, max su centro. */
export function distanceToHeadboxFaceXZ(
  playerXZ: readonly [number, number],
  headbox: ModelViewbox['headbox'],
): number {
  const px = playerXZ[0];
  const pz = playerXZ[1];
  const minX = headbox.min[0];
  const maxX = headbox.max[0];
  const minZ = headbox.min[2];
  const maxZ = headbox.max[2];
  // Distanza al face piu' vicina: min tra |px-minX|, |maxX-px|, |pz-minZ|, |maxZ-pz|.
  const dx = Math.min(Math.abs(px - minX), Math.abs(maxX - px));
  const dz = Math.min(Math.abs(pz - minZ), Math.abs(maxZ - pz));
  return Math.min(dx, dz);
}

/** Direzione radiale outward dal centro AABB in XZ. Pure. */
export function outwardRadialXZ(
  playerXZ: readonly [number, number],
  center: Vec3,
): readonly [number, number] {
  return normalize2([playerXZ[0] - center[0], playerXZ[1] - center[2]]);
}

/** Score "quanto la velocita' del player e' outward rispetto al centro AABB".
 *  Range [-1, 1]. > 0 = sta uscendo. > EDGE_OUTWARD_COSINE_MIN = sta uscendo
 *  decisamente. Pure. */
export function outwardScore(
  velocityXZ: readonly [number, number],
  playerXZ: readonly [number, number],
  center: Vec3,
): number {
  const velMag = mag2(velocityXZ);
  if (velMag < 1e-6) return 0;
  const velNorm = normalize2(velocityXZ);
  const radial = outwardRadialXZ(playerXZ, center);
  return dot2(velNorm, radial);
}

/** Verifica se il player e' in "edge outward" frame state. Tutte le condizioni:
 *  - velocityMag > EDGE_VELOCITY_MIN_MPS
 *  - outwardScore > EDGE_OUTWARD_COSINE_MIN
 *  - distance da AABB face < EDGE_NEAR_FACE_M (sta uscendo, non al centro). */
export function isEdgeOutwardFrame(args: {
  velocityXZ: readonly [number, number];
  playerXZ: readonly [number, number];
  headbox: ModelViewbox['headbox'];
}): boolean {
  const velMag = mag2(args.velocityXZ);
  if (velMag < EDGE_VELOCITY_MIN_MPS) return false;
  const score = outwardScore(args.velocityXZ, args.playerXZ, args.headbox.center);
  if (score < EDGE_OUTWARD_COSINE_MIN) return false;
  const distFace = distanceToHeadboxFaceXZ(args.playerXZ, args.headbox);
  if (distFace > EDGE_NEAR_FACE_M) return false;
  return true;
}

/** Update tracking state + ritorna `true` se persistence >= EDGE_PERSISTENCE_MS.
 *  Pure (muta args.state ma non altro). */
export function trackEdgePersistence(
  state: EdgeTrackingState,
  edgeOutwardNow: boolean,
  nowMs: number,
): boolean {
  if (!edgeOutwardNow) {
    state.outwardSince = null;
    return false;
  }
  if (state.outwardSince === null) {
    state.outwardSince = nowMs;
    return false;
  }
  return nowMs - state.outwardSince >= EDGE_PERSISTENCE_MS;
}

/** Elegge il candidato viewbox piu' allineato con la direzione di velocita'
 *  del player tra le viewbox != current. Filtra per:
 *  - distance < EDGE_CANDIDATE_MAX_M
 *  - cos(velDir, toVbDir) > EDGE_CANDIDATE_COSINE_MIN
 *
 *  Tie-break: massima cosine (= direzione piu' aligned). Su parita' esatta:
 *  primo per index nell'array (deterministico, no flicker random).
 *
 *  Pure. Ritorna arrayIdx del candidato o null. */
export function electCandidateViewbox(args: {
  velocityXZ: readonly [number, number];
  playerXZ: readonly [number, number];
  viewboxes: readonly ModelViewbox[];
  currentArrayIdx: number;
}): number | null {
  const velNorm = normalize2(args.velocityXZ);
  if (velNorm[0] === 0 && velNorm[1] === 0) return null;
  let bestIdx = -1;
  let bestScore = EDGE_CANDIDATE_COSINE_MIN;
  for (let i = 0; i < args.viewboxes.length; i++) {
    if (i === args.currentArrayIdx) continue;
    const vb = args.viewboxes[i];
    if (vb === undefined) continue;
    // PR5 fix 2026-06-05: usa default_camera.pos (spawn point per viewbox)
    // invece di headbox.center. Roma 4vb headbox FULL-SCENE condiviso →
    // headbox.center identico per tutti i vb → edge candidate election non
    // discrimina. default_camera.pos e' calibrato Seurat per ogni viewpoint.
    const toVb: readonly [number, number] = [
      vb.default_camera.pos[0] - args.playerXZ[0],
      vb.default_camera.pos[2] - args.playerXZ[1],
    ];
    const dist = mag2(toVb);
    if (dist > EDGE_CANDIDATE_MAX_M) continue;
    if (dist < 1e-6) continue;
    const toVbNorm = normalize2(toVb);
    const score = dot2(velNorm, toVbNorm);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  return bestIdx >= 0 ? bestIdx : null;
}
